import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';

const IMPORT_ARTISTS_CSV = gql`
  mutation ImportArtistsCSV($file: Upload!) {
    importArtistsCSV(file: $file) {
      success
      message
      totalRows
    }
  }
`;

function ImportArtists() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; totalRows: number } | null>(null);

  const [importArtistsCSV] = useMutation(IMPORT_ARTISTS_CSV);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      const file = acceptedFiles[0];

      if (!file.name.endsWith('.csv')) {
        toast.error(t('import-artists.toast.csv-required'));
        return;
      }

      setUploading(true);
      setResult(null);

      try {
        const response = await importArtistsCSV({
          variables: { file },
        });

        if (response.data?.importArtistsCSV) {
          setResult(response.data.importArtistsCSV);
          if (response.data.importArtistsCSV.success) {
            toast.success(response.data.importArtistsCSV.message);
          }
        }
      } catch (error: any) {
        console.error('Upload error:', error);
        toast.error(error.message || t('import-artists.toast.upload-failed'));
        setResult({
          success: false,
          message: error.message || t('import-artists.toast.upload-failed'),
          totalRows: 0,
        });
      } finally {
        setUploading(false);
      }
    },
    [importArtistsCSV, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    disabled: uploading,
  });

  return (
    <AdminLayout title={t('import-artists.page-title')} description={t('import-artists.page-description')}>
      <div className="space-y-6">
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">{t('import-artists.upload-csv')}</h2>
          <p className="text-sm text-zinc-600 mb-4">{t('import-artists.intro')}</p>

          <div className="mb-6">
            <ol className="text-sm text-zinc-600 space-y-3 list-decimal list-inside">
              <li>
                <a
                  href="/samples/artist-import-sample.csv"
                  download
                  className="text-blue-600 hover:text-blue-700 underline font-medium"
                >
                  {t('import-artists.download-sample')}
                </a>
              </li>
              <li>{t('import-artists.step-prepare')}</li>
              <li>{t('import-artists.step-upload')}</li>
            </ol>
          </div>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors
              ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-zinc-300 bg-zinc-50'}
              ${uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-blue-50 cursor-pointer'}
            `}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center">
              {uploading ? (
                <>
                  <Loader2 className="w-12 h-12 mb-4 text-zinc-400 animate-spin" />
                  <p className="text-sm text-zinc-600">{t('import-artists.dropzone.uploading')}</p>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 mb-4 text-zinc-400" />
                  <p className="text-sm text-zinc-700 mb-2">
                    {isDragActive
                      ? t('import-artists.dropzone.drop-here')
                      : t('import-artists.dropzone.drag-or-browse')}
                  </p>
                  <p className="text-xs text-zinc-500">{t('import-artists.dropzone.csv-only')}</p>
                </>
              )}
            </div>
          </div>

          {result && (
            <div
              className={`mt-4 p-4 rounded-lg border ${
                result.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
              }`}
            >
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {result.success ? tc('success') : t('import-artists.result-error')}
                  </p>
                  <p className="text-sm mt-1">{result.message}</p>
                  {result.success && result.totalRows > 0 && (
                    <p className="text-sm mt-2">{t('import-artists.result-queued', { count: result.totalRows })}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h3 className="text-sm font-medium text-amber-900 mb-2">{t('import-artists.notes-heading')}</h3>
          <ul className="text-sm text-amber-800 space-y-2 list-disc list-inside">
            <li>{t('import-artists.notes.s3-json')}</li>
            <li>{t('import-artists.notes.images')}</li>
            <li>{t('import-artists.notes.ai-metadata')}</li>
            <li>{t('import-artists.notes.background')}</li>
            <li>{t('import-artists.notes.email-results')}</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}

export default ImportArtists;
